(() => {
  const ROOT_SELECTOR = '[data-dk-lux-popup]';

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const money = (cents, format) => {
    const value = Number(cents || 0);
    const token = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    let formatted;

    const withSeparators = (number, precision, thousands, decimal) => {
      const n = (number / 100).toFixed(precision);
      const parts = n.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? parts.join(decimal) : parts[0];
    };

    switch (token) {
      case 'amount_no_decimals':
        formatted = withSeparators(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = withSeparators(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withSeparators(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withSeparators(value, 2, "'", '.');
        break;
      default:
        formatted = withSeparators(value, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  };

  class DKProductPopup {
    constructor(root) {
      if (!root || root.dataset.dkInitialized === 'true') return;
      root.dataset.dkInitialized = 'true';

      this.root = root;
      this.panel = root.querySelector('[data-dk-popup-panel]');
      this.overlay = root.querySelector('[data-dk-popup-overlay]');
      this.closeButtons = root.querySelectorAll('[data-dk-popup-close]');
      this.type = root.dataset.popupType;
      this.designMode = root.dataset.designMode === 'true';
      this.delay = Number(root.dataset.delay || 0) * 1000;
      this.frequency = root.dataset.frequency || 'days';
      this.expiryDays = Number(root.dataset.expiryDays || 7);
      this.storageKey = root.dataset.storageKey || `dk-popup-${root.id}`;
      this.closeOnOverlay = root.dataset.closeOnOverlay === 'true';
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.routeRoot = root.dataset.routeRoot || '/';
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.videoAutoplay = root.dataset.videoAutoplay === 'true';
      this.motionMs = Math.max(100, Number(root.dataset.animationSpeed || 450));
      this.closeBehavior = root.dataset.closeBehavior || 'hide';
      this.reopenUI = root.querySelector('[data-dk-lux-reopen]');
      // Keep popup and reminder out of theme section stacking contexts.
      // Do not relocate the section inside Shopify's visual editor.
      if (!this.designMode && document.body) {
        document.body.appendChild(this.root);
        if (this.reopenUI) document.body.appendChild(this.reopenUI);
      }
      this.reopenUI?.querySelector('[data-dk-lux-reopen-open]')?.addEventListener('click', () => { this.reopenUI.hidden = true; this.open(); });
      this.reopenUI?.querySelector('[data-dk-lux-reopen-dismiss]')?.addEventListener('click', () => { this.reopenUI.hidden = true; this.markSeen(); });
      this.lastFocused = null;
      this.previousOverflow = null;
      this.openTimer = null;
      this.opened = false;
      this.bundleItems = new Map();

      this.bindBase();
      this.initBundle();
      this.initPromoCodes();
      this.schedule();
    }

    bindBase() {
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true)));

      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          if (this.closeOnOverlay) this.close(true);
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.opened) this.close(true);
      });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target && event.target.querySelector(`#${CSS.escape(this.root.id)}`)) this.open();
          if (event.detail && event.detail.sectionId && this.root.id.includes(event.detail.sectionId)) this.open();
        });
      }
    }

    schedule() {
      if (!this.designMode && this.shouldSuppress()) return;
      const actualDelay = this.designMode ? 250 : this.delay;
      this.openTimer = window.setTimeout(() => { if (this.root.isConnected) this.open(); }, actualDelay);
    }

    shouldSuppress() {
      if (this.frequency === 'always') return false;

      try {
        if (this.frequency === 'session') {
          return sessionStorage.getItem(this.storageKey) === '1';
        }

        const stored = Number(localStorage.getItem(this.storageKey) || 0);
        if (!stored) return false;
        const maxAge = this.expiryDays * 86400000;
        return Date.now() - stored < maxAge;
      } catch (_) {
        return false;
      }
    }

    markSeen() {
      if (this.designMode || this.frequency === 'always') return;
      try {
        if (this.frequency === 'session') sessionStorage.setItem(this.storageKey, '1');
        else localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (_) {}
    }

    open() {
      if (this.opened || !this.root.isConnected) return;
      // Guard also against an accidentally included global script on non-product pages.
      if (!this.designMode && this.root.dataset.pageType !== 'product') return;
      this.lastFocused = document.activeElement;
      this.root.hidden = false;
      if (this.reopenUI) this.reopenUI.hidden = true;
      requestAnimationFrame(() => this.root.classList.add('is-open'));
      this.opened = true;

      if (!this.root.classList.contains('is-floating')) {
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }

      const focusable = this.root.querySelector('button, a, select, input, [tabindex]:not([tabindex="-1"])');
      if (focusable) window.setTimeout(() => focusable.focus({ preventScroll: true }), 80);

      this.playVideos();
      if (this.type === 'scratch') this.initScratch();
    }

    close(markSeen = true, allowReopen = true) {
      if (!this.opened) return;
      if (markSeen) this.markSeen();
      this.root.classList.remove('is-open');
      this.opened = false;
      if (!this.root.classList.contains('is-floating')) document.documentElement.style.overflow = this.previousOverflow ?? '';
      this.pauseVideos();

      window.setTimeout(() => {
        if (!this.opened) {
          this.root.hidden = true;
          if (allowReopen && this.closeBehavior === 'minimize' && this.reopenUI) {
            // The reminder is outside modal visibility: move to body to remain accessible.
            if (this.reopenUI.parentElement === this.root) document.body.appendChild(this.reopenUI);
            this.reopenUI.hidden = false;
          }
        }
      }, this.motionMs);

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus({ preventScroll: true });
    }

    playVideos() {
      if (!this.videoAutoplay) return;
      this.root.querySelectorAll('video.dk-popup-video-native').forEach((video) => {
        video.play().catch(() => {});
      });
    }

    pauseVideos() {
      this.root.querySelectorAll('video.dk-popup-video-native').forEach((video) => video.pause());
    }

    discountUrl(code, redirect) {
      const root = this.routeRoot.endsWith('/') ? this.routeRoot : `${this.routeRoot}/`;
      return `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect || this.cartUrl)}`;
    }

    initPromoCodes() {
      this.root.querySelectorAll('[data-dk-discount-code]').forEach((link) => {
        link.addEventListener('click', (event) => {
          const code = link.dataset.dkDiscountCode;
          if (!code) return;
          event.preventDefault();
          this.markSeen();
          const redirect = link.getAttribute('href') || this.cartUrl;
          window.location.href = this.discountUrl(code, redirect);
        });
      });
    }

    initBundle() {
      if (this.type !== 'bundle') return;

      this.bundleGrid = this.root.querySelector('[data-dk-bundle-grid]');
      this.bundleSummaryItems = this.root.querySelector('[data-dk-bundle-items]');
      this.bundleEmpty = this.root.querySelector('[data-dk-bundle-empty]');
      this.bundleStatus = this.root.querySelector('[data-dk-bundle-status]');
      this.bundleSubtotal = this.root.querySelector('[data-dk-bundle-subtotal]');
      this.bundleTotal = this.root.querySelector('[data-dk-bundle-total]');
      this.bundleSaving = this.root.querySelector('[data-dk-bundle-saving]');
      this.bundleSubmit = this.root.querySelector('[data-dk-bundle-submit]');
      this.bundleError = this.root.querySelector('[data-dk-bundle-error]');

      this.bundleSuccess = this.root.querySelector('[data-dk-bundle-success]');
      this.afterAdd = this.root.dataset.afterAdd || 'stay';
      this.selectLabel = this.root.dataset.selectLabel || 'Select';
      this.selectedLabel = this.root.dataset.selectedLabel || 'Selected';
      this.emptyLabel = this.root.dataset.emptyLabel || 'Your bundle is empty.';
      this.itemsLabel = this.root.dataset.itemsLabel || '{count} items in your bundle';
      this.unlockLabel = this.root.dataset.unlockLabel || 'Add {count} more to unlock {discount}%';
      this.unlockedLabel = this.root.dataset.unlockedLabel || '{discount}% discount unlocked';

      this.tierCount = Number(this.root.dataset.tierCount || 3);
      this.discountMode = this.root.dataset.discountMode || 'code';
      this.tiers = Array.from({ length: this.tierCount }, (_, i) => i + 1).map((index) => ({
        index,
        min: Number(this.root.dataset[`tier${index}Min`] || 0),
        percent: Number(this.root.dataset[`tier${index}Percent`] || 0),
        code: this.root.dataset[`tier${index}Code`] || ''
      })).filter((tier) => tier.min > 0 && tier.percent > 0).sort((a, b) => a.min - b.min);

      this.root.querySelectorAll('[data-dk-bundle-card]').forEach((card) => this.bindBundleCard(card));
      this.bundleSummaryItems?.addEventListener('click', (event) => this.onSummaryClick(event));
      this.bundleSubmit?.addEventListener('click', () => this.addBundleToCart());
      this.renderBundle();
    }

    bindBundleCard(card) {
      const select = card.querySelector('[data-dk-variant-select]');
      const button = card.querySelector('[data-dk-bundle-toggle]');

      if (select) {
        select.addEventListener('change', () => {
          const previousVariant = card.dataset.selectedVariantId;
          this.updateCardVariant(card);
          if (card.classList.contains('is-selected')) {
            if (previousVariant) this.bundleItems.delete(String(previousVariant));
            const item = this.itemFromCard(card);
            if (item) {
              this.bundleItems.set(String(item.id), item);
              card.dataset.selectedVariantId = String(item.id);
            }
            this.renderBundle();
          }
        });
        this.updateCardVariant(card);
      }

      button?.addEventListener('click', () => {
        const item = this.itemFromCard(card);
        if (!item) return;
        const key = String(item.id);

        if (card.classList.contains('is-selected')) {
          const selectedKey = card.dataset.selectedVariantId || key;
          this.bundleItems.delete(String(selectedKey));
          card.classList.remove('is-selected');
          card.dataset.selectedVariantId = '';
          button.textContent = this.selectLabel;
        } else {
          this.bundleItems.set(key, item);
          card.classList.add('is-selected');
          card.dataset.selectedVariantId = key;
          button.textContent = this.selectedLabel;
        }

        this.renderBundle();
      });
    }

    updateCardVariant(card) {
      const select = card.querySelector('[data-dk-variant-select]');
      if (!select) return;
      const option = select.options[select.selectedIndex];
      const price = card.querySelector('[data-dk-card-price]');
      const compare = card.querySelector('[data-dk-card-compare]');
      const image = card.querySelector('.dk-bundle-card__image');

      if (price) price.textContent = money(option.dataset.price, this.moneyFormat);
      if (compare) {
        const comparePrice = Number(option.dataset.compare || 0);
        compare.textContent = comparePrice > Number(option.dataset.price || 0) ? money(comparePrice, this.moneyFormat) : '';
      }
      if (image && option.dataset.image) {
        image.removeAttribute('srcset');
        image.src = option.dataset.image;
      }
    }

    itemFromCard(card) {
      const select = card.querySelector('[data-dk-variant-select]');
      const option = select ? select.options[select.selectedIndex] : null;
      const variantId = option ? option.value : card.dataset.variantId;
      if (!variantId) return null;

      return {
        id: Number(variantId),
        productId: Number(card.dataset.productId),
        title: card.dataset.productTitle || '',
        variantTitle: option ? (option.dataset.variantTitle || '') : (card.dataset.variantTitle || ''),
        price: Number(option ? option.dataset.price : card.dataset.price || 0),
        image: option?.dataset.image || card.dataset.image || '',
        qty: 1,
        card
      };
    }

    onSummaryClick(event) {
      const action = event.target.closest('[data-dk-bundle-action]');
      if (!action) return;
      const id = String(action.dataset.variantId || '');
      const item = this.bundleItems.get(id);
      if (!item) return;

      switch (action.dataset.dkBundleAction) {
        case 'plus':
          item.qty += 1;
          break;
        case 'minus':
          item.qty -= 1;
          if (item.qty <= 0) this.removeBundleItem(id, item);
          break;
        case 'remove':
          this.removeBundleItem(id, item);
          break;
      }

      if (item.qty > 0 && this.bundleItems.has(id)) this.bundleItems.set(id, item);
      this.renderBundle();
    }

    removeBundleItem(id, item) {
      this.bundleItems.delete(String(id));
      if (item.card) {
        item.card.classList.remove('is-selected');
        item.card.dataset.selectedVariantId = '';
        const button = item.card.querySelector('[data-dk-bundle-toggle]');
        if (button) button.textContent = this.selectLabel;
      }
    }

    bundleStats() {
      const items = Array.from(this.bundleItems.values());
      const qty = items.reduce((sum, item) => sum + item.qty, 0);
      const subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
      const distinctCount = items.length;
      const tier = this.tiers.filter((candidate) => distinctCount >= candidate.min).sort((a,b) => b.min-a.min)[0] || null;
      const nextTier = this.tiers.find((candidate) => distinctCount < candidate.min) || null;
      const discount = tier ? Math.round(subtotal * tier.percent / 100) : 0;
      return { items, qty, distinctCount, subtotal, tier, nextTier, discount, total: subtotal - discount };
    }

    renderBundle() {
      if (!this.bundleSummaryItems) return;
      const stats = this.bundleStats();

      this.bundleEmpty.hidden = stats.qty > 0;
      this.bundleSummaryItems.innerHTML = stats.items.map((item) => `
        <div class="dk-bundle-summary__item">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="">` : '<span></span>'}
          <div>
            <div class="dk-bundle-summary__item-title">${escapeHtml(item.title)}</div>
            ${item.variantTitle && item.variantTitle !== 'Default Title' ? `<div class="dk-bundle-summary__item-variant">${escapeHtml(item.variantTitle)}</div>` : ''}
            <div class="dk-bundle-summary__item-price">${money(item.price * item.qty, this.moneyFormat)}</div>
            <button type="button" class="dk-bundle-summary__remove" data-dk-bundle-action="remove" data-variant-id="${item.id}">Entfernen</button>
          </div>
          <div class="dk-bundle-summary__qty">
            <button type="button" aria-label="Decrease quantity" data-dk-bundle-action="minus" data-variant-id="${item.id}">−</button>
            <span>${item.qty}</span>
            <button type="button" aria-label="Increase quantity" data-dk-bundle-action="plus" data-variant-id="${item.id}">+</button>
          </div>
        </div>
      `).join('');

      const visibleTiers = [...this.root.querySelectorAll('[data-dk-tier]')].slice(0, this.tierCount);
      const progressTarget = this.tiers.length ? this.tiers[this.tiers.length - 1].min : 1;
      this.root.querySelector('[data-dk-bundle-tiers]')?.style.setProperty('--dk-active-tiers', String(Math.max(1, visibleTiers.length)));
      this.root.querySelectorAll('[data-dk-tier]').forEach((tierEl, index) => {
        const min = Number(tierEl.dataset.min || 0);
        const prior = index ? Number(visibleTiers[index - 1]?.dataset.min || 0) : 0;
        const fraction = min > prior ? Math.max(0, Math.min(1, (stats.distinctCount - prior) / (min - prior))) : 0;
        tierEl.hidden = index >= this.tierCount;
        tierEl.style.setProperty('--dk-tier-progress', `${fraction * 100}%`);
        tierEl.classList.toggle('is-unlocked', index < this.tierCount && stats.distinctCount >= min && min > 0);
        tierEl.classList.toggle('is-in-progress', index < this.tierCount && fraction > 0 && fraction < 1);
        tierEl.setAttribute('aria-label', `${tierEl.textContent.trim()}: ${Math.round(fraction*100)}% erreicht`);
      });

      if (stats.tier) {
        this.bundleStatus.textContent = this.unlockedLabel.replace('{discount}', stats.tier.percent);
      } else if (stats.nextTier) {
        this.bundleStatus.textContent = this.unlockLabel
          .replace('{count}', Math.max(0, stats.nextTier.min - stats.distinctCount))
          .replace('{discount}', stats.nextTier.percent);
      } else {
        this.bundleStatus.textContent = this.emptyLabel;
      }

      this.bundleSubtotal.textContent = money(stats.subtotal, this.moneyFormat);
      this.bundleSubtotal.classList.toggle('dk-lux-crossed', stats.discount > 0);
      this.bundleTotal.textContent = money(stats.total, this.moneyFormat);

      this.bundleTotal.classList.remove('dk-price-update');
      void this.bundleTotal.offsetWidth;
      this.bundleTotal.classList.add('dk-price-update');
      this.bundleTotal.title = 'Voraussichtlicher Betrag. Der tatsächliche Rabatt wird von Shopify im Warenkorb/Checkout geprüft.';
      this.bundleSaving.textContent = stats.discount > 0
        ? `${this.root.dataset.savingCheck === 'true' ? '✓ ' : ''}${this.root.dataset.savingLabel || 'Du sparst'} ${money(stats.discount, this.moneyFormat)}${this.root.dataset.savingPercent === 'true' ? ` (${stats.tier.percent}%)` : ''}`
        : '';
      this.bundleSaving.hidden = !stats.discount;

      this.bundleSubmit.disabled = stats.qty === 0 || !stats.tier;
      this.bundleSubmit.dataset.discountCode = stats.tier?.code || '';
      this.bundleSubmit.dataset.discountPercent = stats.tier?.percent || '0';
    }

    async addBundleToCart() {
      const stats = this.bundleStats();
      if (!stats.items.length || !this.bundleSubmit) return;

      this.bundleSubmit.disabled = true;
      this.bundleSubmit.setAttribute('aria-busy', 'true');
      if (this.bundleError) this.bundleError.classList.remove('is-visible');
      if (this.bundleSuccess) this.bundleSuccess.hidden = true;

      try {
        const cartEndpoint = `${this.routeRoot.replace(/\/?$/, '/')}cart.js`;
        const beforeResponse = await fetch(cartEndpoint, { credentials: 'same-origin', cache: 'no-store' });
        if (!beforeResponse.ok) throw new Error('Warenkorb konnte nicht geladen werden.');
        const beforeCart = await beforeResponse.json();
        const beforeQuantities = new Map();
        beforeCart.items.forEach(line => beforeQuantities.set(Number(line.variant_id), (beforeQuantities.get(Number(line.variant_id)) || 0) + Number(line.quantity)));
        const response = await fetch(`${this.routeRoot.replace(/\/?$/, '/')}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: stats.items.map((item) => ({ id: item.id, quantity: item.qty }))
          })
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || 'Could not add bundle to cart.');
        }

        // Ensure Shopify confirms the added variants in a fresh cart read.
        const cartResponse = await fetch(`${this.routeRoot.replace(/\/?$/, '/')}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Warenkorb-Bestätigung nicht möglich. Bitte Warenkorb prüfen.');
        const verifiedCart = await cartResponse.json();
        const requested = stats.items.map(item => ({ id: Number(item.id), qty: Number(item.qty) }));
        const confirmed = requested.every(req =>
          verifiedCart.items.filter(line => Number(line.variant_id) === req.id).reduce((sum, line) => sum + Number(line.quantity), 0) >= (beforeQuantities.get(req.id) || 0) + req.qty
        );
        if (!confirmed) throw new Error('Artikel konnten im Warenkorb nicht bestätigt werden. Bitte Warenkorb prüfen.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: verifiedCart, source: 'dk-lux-popup' } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: verifiedCart, source: 'dk-lux-popup' } }));
        this.markSeen();
        const code = this.discountMode === 'code' ? (stats.tier?.code || '') : '';
        if (this.afterAdd === 'open_cart') {
          window.location.href = code ? this.discountUrl(code, this.cartUrl) : this.cartUrl;
          return;
        }
        // Applying a Shopify discount code requires navigation to /discount/CODE.
        // In stay-on-page mode, redirect back to the current product page rather than opening cart.
        if (code) {
          window.location.href = this.discountUrl(code, window.location.pathname + window.location.search);
          return;
        }
        this.bundleItems.clear();
        this.root.querySelectorAll('[data-dk-bundle-card]').forEach((card) => {
          card.classList.remove('is-selected');
          card.dataset.selectedVariantId = '';
          const button = card.querySelector('[data-dk-bundle-toggle]');
          if (button) button.textContent = this.selectLabel;
        });
        this.renderBundle();
        this.bundleSubmit.removeAttribute('aria-busy');
        if (this.afterAdd === 'cart_drawer') {
          const trigger = document.querySelector('[aria-controls="CartDrawer"], [aria-controls="cart-drawer"]');
          if (trigger) { trigger.click(); this.close(true, false); } else this.close(true, false);
        } else {
          this.close(true, false);
        }
        const notice = document.createElement('div');
        notice.className = 'dk-lux-cart-toast';
        notice.setAttribute('role','status');
        notice.textContent = this.root.dataset.successText || 'Produkte zum Warenkorb hinzugefügt';
        document.body.appendChild(notice);
        requestAnimationFrame(() => notice.classList.add('is-visible'));
        setTimeout(() => { notice.classList.remove('is-visible'); setTimeout(() => notice.remove(), 350); }, 4500);
      } catch (error) {
        if (this.bundleError) {
          this.bundleError.textContent = error.message || 'Could not add bundle to cart.';
          this.bundleError.classList.add('is-visible');
        }
        this.bundleSubmit.disabled = false;
        this.bundleSubmit.removeAttribute('aria-busy');
      }
    }

    initScratch() {
      const wrapper = this.root.querySelector('[data-dk-scratch]');
      const canvas = wrapper?.querySelector('canvas');
      if (!wrapper || !canvas || canvas.dataset.ready === 'true') return;
      canvas.dataset.ready = 'true';

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const threshold = Number(wrapper.dataset.threshold || 45);
      const coverColor = wrapper.dataset.coverColor || '#c7a57d';
      const coverText = wrapper.dataset.coverText || 'SCRATCH HERE';
      const coverImage = wrapper.dataset.coverImage || '';
      let drawing = false;
      let strokeCounter = 0;

      const drawCover = () => {
        const rect = canvas.getBoundingClientRect();
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        canvas.width = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = coverColor;
        ctx.fillRect(0, 0, rect.width, rect.height);

        const drawText = () => {
          ctx.fillStyle = 'rgba(255,255,255,.92)';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = `700 ${Math.max(18, Math.min(34, rect.width / 9))}px Arial, sans-serif`;
          ctx.fillText(coverText, rect.width / 2, rect.height / 2);
        };

        if (coverImage) {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            const scale = Math.max(rect.width / image.width, rect.height / image.height);
            const width = image.width * scale;
            const height = image.height * scale;
            ctx.drawImage(image, (rect.width - width) / 2, (rect.height - height) / 2, width, height);
            ctx.fillStyle = 'rgba(0,0,0,.14)';
            ctx.fillRect(0, 0, rect.width, rect.height);
            drawText();
          };
          image.onerror = drawText;
          image.src = coverImage;
        } else {
          drawText();
        }
      };

      const scratchAt = (event) => {
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const radius = Math.max(18, rect.width * .055);
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
        strokeCounter += 1;
        if (strokeCounter % 8 === 0) checkReveal();
      };

      const checkReveal = () => {
        try {
          const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          let transparent = 0;
          let sampled = 0;
          for (let i = 3; i < pixels.length; i += 64) {
            sampled += 1;
            if (pixels[i] < 40) transparent += 1;
          }
          if (sampled && (transparent / sampled) * 100 >= threshold) reveal();
        } catch (_) {
          if (strokeCounter > 45) reveal();
        }
      };

      const reveal = () => {
        wrapper.classList.add('is-revealed');
        canvas.style.pointerEvents = 'none';
        const claim = wrapper.querySelector('[data-dk-scratch-claim]');
        if (claim) claim.hidden = false;
      };

      canvas.addEventListener('pointerdown', (event) => {
        drawing = true;
        canvas.setPointerCapture?.(event.pointerId);
        scratchAt(event);
      });
      canvas.addEventListener('pointermove', (event) => {
        if (drawing) scratchAt(event);
      });
      canvas.addEventListener('pointerup', () => { drawing = false; checkReveal(); });
      canvas.addEventListener('pointercancel', () => { drawing = false; });
      wrapper.querySelector('[data-dk-scratch-skip]')?.addEventListener('click', reveal);

      drawCover();
      window.addEventListener('resize', () => {
        if (!wrapper.classList.contains('is-revealed')) drawCover();
      }, { passive: true });
    }
  }

  const init = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new DKProductPopup(root));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
